import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { HabSelectOption } from "@livereader/habitus-vue";
import { listProjectVersions, type ProjectVersionResponse } from "@/api";
import {
	defaultVersion,
	versionOptions,
	type VersionOption,
} from "@operator/utils/dataspace/projectVersions";

/**
 * The versions a project's dataspace can run, as a picker's options — for creating a dataspace
 * and for its settings. Read again whenever `projectId` changes; only the newest read lands.
 *
 * `GET /projects/{id}/versions` asks for `designer:release:read` on the project, which someone
 * editing a dataspace need not have: they may not be a member of its project at all. Then
 * `fallback` answers instead — the builds the dataspace itself can see — and failing that the
 * picker still offers `wip` and what the dataspace runs now.
 */
export function useProjectVersionOptions(
	projectId: () => string | null | undefined,
	options: {
		/** What the dataspace runs now, kept on the list whatever happened to its build. */
		current?: () => string | null | undefined;
		fallback?: () => ProjectVersionResponse[];
	} = {},
) {
	const { t } = useI18n();
	const versions = ref<ProjectVersionResponse[]>([]);
	const loading = ref(false);
	/** The versions of the current `projectId` have been read (or given up on). */
	const known = ref(false);

	let generation = 0;
	watch(
		projectId,
		async (id) => {
			const mine = ++generation;
			versions.value = [];
			known.value = false;
			if (!id) {
				loading.value = false;
				return;
			}
			loading.value = true;
			try {
				const list = await listProjectVersions(id);
				if (mine === generation) versions.value = list;
			} catch {
				if (mine === generation) versions.value = options.fallback?.() ?? [];
			} finally {
				if (mine === generation) {
					loading.value = false;
					known.value = true;
				}
			}
		},
		{ immediate: true },
	);

	function label(option: VersionOption): string {
		switch (option.kind) {
			case "wip":
				return t("operator.dataspace.versionWip");
			case "current":
				return t("operator.dataspace.versionCurrent", { version: option.version });
			case "release":
				return option.title
					? t("operator.dataspace.versionReleaseTitled", {
							version: option.version,
							title: option.title,
						})
					: t("operator.dataspace.versionRelease", { version: option.version });
		}
	}

	const selectOptions = computed<HabSelectOption[]>(() =>
		versionOptions(versions.value, options.current?.()).map((option) => ({
			value: option.value,
			label: label(option),
		})),
	);

	/** What a new dataspace starts on: the latest runnable release, else `wip`. */
	const preferred = computed(() => defaultVersion(versions.value));

	return { versions, options: selectOptions, preferred, loading, known };
}
